/**
 * Backups store module — `/my/backups`.
 *
 * Holds the org-scoped lists for schedules, jobs (run history), targets (per-org
 * remote destinations), restores (run history), and the on-demand DB instance
 * discovery (cluster-api bridge). The page itself is auth-gated; this module
 * only dispatches loads when the user navigates in or opens a specific tab.
 *
 * Namespaced, NOT persisted — backup data is always re-fetched.
 *
 * The store deliberately distinguishes three categories of failure raised by
 * the service layer, so the UI can render specific copy:
 *   - `BackupBridgeNotReady` (typed)  → `dbInstances.bridgeReady = false` and
 *     the schedule wizard falls back to a manual host/port form.
 *   - `BackupAdapterStub` (typed)     → surfaced via toast + per-target "stub"
 *     badge; the schedule wizard hints that S3 / GCS are coming soon.
 *   - `BackupServiceUnavailable` (typed) → `serviceAvailable = false`: this
 *     face has no backup-api (flag off, or a non-JSON 404 from the edge). The
 *     list loads resolve EMPTY and do not throw, so the Dashboard logs nothing
 *     and `/my/backups` shows "not available on this environment".
 *   - everything else                 → a section-specific error string used by
 *     the inline retry banner.
 *
 * Per-row "is running" state lives in `runtimeBusy`, keyed by
 * `${scope}:${id}:${action}` (e.g. `schedule:abc:run-now`, `job:xyz:download`,
 * `restore:def:perform`). The UI checks `runtimeBusy[key]` to disable per-row
 * buttons without holding component-local mirrors.
 */
import {
  backupApi,
  BackupServiceUnavailable,
  BackupBridgeNotReady,
  BackupAdapterStub,
  RestoreConfirmationRejected
} from '@/services/backup-api'

const state = () => ({
  // ── Is backup-api behind this face at all? ────────────────────────────
  // Flips to false on the first BackupServiceUnavailable; stays false for the
  // page's life (the face does not grow a service mid-session).
  serviceAvailable: true,

  // ── Schedules ──────────────────────────────────────────────────────────
  schedules: [],
  // True once a schedules read has SUCCEEDED — the Dashboard's "Schedule your
  // first backup" nudge needs a real empty list, not a failed one.
  schedulesLoaded: false,
  schedulesCount: 0,
  schedulesLoading: false,
  schedulesError: null,
  schedulePage: 1,
  schedulePageSize: 50,

  // ── Jobs (run history) ────────────────────────────────────────────────
  jobs: [],
  jobsCount: 0,
  jobsLoading: false,
  jobsError: null,
  jobFilter: null,   // status filter ('success'|'failed'|'running'|...)
  jobPage: 1,
  jobPageSize: 50,

  // ── Targets (per-org remote destinations) ─────────────────────────────
  targets: [],
  targetsLoading: false,
  targetsError: null,

  // ── Restores (run history) ────────────────────────────────────────────
  restores: [],
  restoresCount: 0,
  restoresLoading: false,
  restoresError: null,

  // ── Managed-DB instance discovery (cluster-api bridge) ────────────────
  // Loaded on-demand when the schedule wizard reaches the "database" step.
  // `bridgeReady` is false until we successfully fetch — a 503 from the
  // service surfaces as `BackupBridgeNotReady` and we keep it false so the
  // wizard knows to render the manual fallback form.
  dbInstances: [],
  dbInstancesLoading: false,
  dbInstancesError: null,
  bridgeReady: false,

  // ── Per-row busy map (run-now / enable / disable / download / restore) ─
  runtimeBusy: {}
})

const getters = {
  scheduleById: (s) => (id) => s.schedules.find((row) => row.id === id) || null,
  defaultTarget: (s) => s.targets.find((t) => t.default || t.is_default) || null,

  /** Most-used schedule for the Overview "Run now" shortcut. We pick the
   *  schedule with the largest run_count (or last_run_at, whichever the
   *  backend exposes). Falls back to the first enabled row. */
  mostUsedSchedule: (s) => {
    const enabled = s.schedules.filter((row) => row.enabled)
    if (!enabled.length) return null
    const ranked = [...enabled].sort((a, b) => {
      const ar = a.run_count ?? 0
      const br = b.run_count ?? 0
      if (ar !== br) return br - ar
      const at = a.last_run_at ? Date.parse(a.last_run_at) : 0
      const bt = b.last_run_at ? Date.parse(b.last_run_at) : 0
      return bt - at
    })
    return ranked[0] || enabled[0]
  },

  /** A sorted snapshot of jobs for the Overview recent-runs strip. */
  recentJobs: (s) => {
    return [...s.jobs]
      .sort((a, b) => {
        const at = a.started_at ? Date.parse(a.started_at) : 0
        const bt = b.started_at ? Date.parse(b.started_at) : 0
        return bt - at
      })
      .slice(0, 5)
  },

  /** Sum of size_bytes across jobs we've loaded — used by the storage hint
   *  in the Overview. The backend will eventually expose this on a
   *  /storage/stats endpoint; this is a best-effort placeholder. */
  totalStoredBytes: (s) => {
    return s.jobs.reduce((acc, j) => acc + (Number(j.size_bytes) || 0), 0)
  }
}

function makeBusyKey(scope, id, action) {
  return `${scope}:${id}:${action}`
}

const mutations = {
  SET_SERVICE_UNAVAILABLE(state) { state.serviceAvailable = false },
  // Schedules
  SET_SCHEDULES_LOADING(state, v) { state.schedulesLoading = v },
  SET_SCHEDULES_ERROR(state, v) { state.schedulesError = v },
  SET_SCHEDULES(state, { items, count }) {
    state.schedulesLoaded = true
    state.schedules = Array.isArray(items) ? items : []
    state.schedulesCount = count ?? state.schedules.length
  },
  UPSERT_SCHEDULE(state, schedule) {
    if (!schedule || !schedule.id) return
    const idx = state.schedules.findIndex((s) => s.id === schedule.id)
    if (idx === -1) state.schedules = [schedule, ...state.schedules]
    else {
      const next = [...state.schedules]
      next.splice(idx, 1, { ...next[idx], ...schedule })
      state.schedules = next
    }
  },
  REMOVE_SCHEDULE(state, id) {
    state.schedules = state.schedules.filter((s) => s.id !== id)
  },

  // Jobs
  SET_JOBS_LOADING(state, v) { state.jobsLoading = v },
  SET_JOBS_ERROR(state, v) { state.jobsError = v },
  SET_JOBS(state, { items, count }) {
    state.jobs = Array.isArray(items) ? items : []
    state.jobsCount = count ?? state.jobs.length
  },
  SET_JOB_FILTER(state, v) { state.jobFilter = v || null },
  SET_JOB_PAGE(state, v) { state.jobPage = v || 1 },
  SET_JOB_PAGE_SIZE(state, v) { state.jobPageSize = v || 50 },
  UPSERT_JOB(state, job) {
    if (!job || !(job.id || job.job_id)) return
    const id = job.id || job.job_id
    const idx = state.jobs.findIndex((j) => (j.id || j.job_id) === id)
    if (idx === -1) state.jobs = [job, ...state.jobs]
    else {
      const next = [...state.jobs]
      next.splice(idx, 1, { ...next[idx], ...job })
      state.jobs = next
    }
  },

  // Targets
  SET_TARGETS_LOADING(state, v) { state.targetsLoading = v },
  SET_TARGETS_ERROR(state, v) { state.targetsError = v },
  SET_TARGETS(state, v) { state.targets = Array.isArray(v) ? v : [] },
  UPSERT_TARGET(state, target) {
    if (!target || !target.id) return
    const idx = state.targets.findIndex((t) => t.id === target.id)
    if (idx === -1) state.targets = [target, ...state.targets]
    else {
      const next = [...state.targets]
      next.splice(idx, 1, { ...next[idx], ...target })
      state.targets = next
    }
  },
  REMOVE_TARGET(state, id) {
    state.targets = state.targets.filter((t) => t.id !== id)
  },

  // Restores
  SET_RESTORES_LOADING(state, v) { state.restoresLoading = v },
  SET_RESTORES_ERROR(state, v) { state.restoresError = v },
  SET_RESTORES(state, { items, count }) {
    state.restores = Array.isArray(items) ? items : []
    state.restoresCount = count ?? state.restores.length
  },
  UPSERT_RESTORE(state, restore) {
    if (!restore || !(restore.id || restore.restore_id)) return
    const id = restore.id || restore.restore_id
    const idx = state.restores.findIndex(
      (r) => (r.id || r.restore_id) === id
    )
    if (idx === -1) state.restores = [restore, ...state.restores]
    else {
      const next = [...state.restores]
      next.splice(idx, 1, { ...next[idx], ...restore })
      state.restores = next
    }
  },

  // DB instances
  SET_DB_INSTANCES_LOADING(state, v) { state.dbInstancesLoading = v },
  SET_DB_INSTANCES_ERROR(state, v) { state.dbInstancesError = v },
  SET_DB_INSTANCES(state, v) {
    state.dbInstances = Array.isArray(v) ? v : []
  },
  SET_BRIDGE_READY(state, v) { state.bridgeReady = !!v },

  // Per-row busy
  SET_RUNTIME_BUSY(state, { scope, id, action, busy }) {
    const key = makeBusyKey(scope, id, action)
    const next = { ...state.runtimeBusy }
    if (busy) next[key] = true
    else delete next[key]
    state.runtimeBusy = next
  }
}

const EMPTY_PAGE = Object.freeze({ items: [], count: 0, pagination: null })

/** No backup-api on this face: remember it and resolve the load empty. */
function unavailable(commit, empty) {
  commit('SET_SERVICE_UNAVAILABLE')
  return empty
}

const actions = {
  // ── Schedules ──────────────────────────────────────────────────────────
  async loadSchedules({ commit, state }, filters = {}) {
    commit('SET_SCHEDULES_LOADING', true)
    commit('SET_SCHEDULES_ERROR', null)
    try {
      const result = await backupApi.listSchedules({
        type: filters.type,
        kind: filters.kind,
        enabled: filters.enabled,
        page: filters.page || state.schedulePage,
        pageSize: filters.pageSize || state.schedulePageSize
      })
      commit('SET_SCHEDULES', result)
      return result
    } catch (error) {
      if (error instanceof BackupServiceUnavailable) return unavailable(commit, EMPTY_PAGE)
      commit('SET_SCHEDULES_ERROR', error?.message || 'Failed to load schedules')
      throw error
    } finally {
      commit('SET_SCHEDULES_LOADING', false)
    }
  },

  async createSchedule({ commit, dispatch }, payload) {
    try {
      const schedule = await backupApi.createSchedule(payload)
      commit('UPSERT_SCHEDULE', schedule)
      dispatch('showNotification', {
        type: 'success',
        message: `Schedule “${payload.name || 'backup'}” created.`
      }, { root: true })
      return schedule
    } catch (error) {
      dispatch('showNotification', {
        type: 'error',
        message: error?.message || 'Couldn’t create that schedule.'
      }, { root: true })
      throw error
    }
  },

  async updateSchedule({ commit, dispatch }, { id, payload }) {
    try {
      const schedule = await backupApi.updateSchedule(id, payload)
      commit('UPSERT_SCHEDULE', schedule)
      dispatch('showNotification', {
        type: 'success',
        message: 'Schedule updated.'
      }, { root: true })
      return schedule
    } catch (error) {
      dispatch('showNotification', {
        type: 'error',
        message: error?.message || 'Couldn’t update that schedule.'
      }, { root: true })
      throw error
    }
  },

  async deleteSchedule({ commit, dispatch }, id) {
    commit('SET_RUNTIME_BUSY', { scope: 'schedule', id, action: 'delete', busy: true })
    try {
      await backupApi.deleteSchedule(id)
      commit('REMOVE_SCHEDULE', id)
      dispatch('showNotification', {
        type: 'success',
        message: 'Schedule deleted.'
      }, { root: true })
    } catch (error) {
      dispatch('showNotification', {
        type: 'error',
        message: error?.message || 'Couldn’t delete that schedule.'
      }, { root: true })
      throw error
    } finally {
      commit('SET_RUNTIME_BUSY', { scope: 'schedule', id, action: 'delete', busy: false })
    }
  },

  async runScheduleNow({ commit, dispatch }, id) {
    commit('SET_RUNTIME_BUSY', { scope: 'schedule', id, action: 'run-now', busy: true })
    try {
      const job = await backupApi.runNow(id)
      // Push the new job onto the history so it shows up immediately.
      if (job) commit('UPSERT_JOB', job)
      dispatch('showNotification', {
        type: 'success',
        message: 'Backup job queued. It will appear in History.'
      }, { root: true })
      return job
    } catch (error) {
      dispatch('showNotification', {
        type: 'error',
        message: error?.message || 'Couldn’t queue that backup.'
      }, { root: true })
      throw error
    } finally {
      commit('SET_RUNTIME_BUSY', { scope: 'schedule', id, action: 'run-now', busy: false })
    }
  },

  async toggleSchedule({ commit, dispatch }, { id, enabled }) {
    const action = enabled ? 'enable' : 'disable'
    commit('SET_RUNTIME_BUSY', { scope: 'schedule', id, action, busy: true })
    try {
      const schedule = enabled
        ? await backupApi.enableSchedule(id)
        : await backupApi.disableSchedule(id)
      // Backend may return the updated row or just {success:true}; either
      // way reflect the toggle locally so the UI doesn't lag.
      if (schedule && schedule.id) {
        commit('UPSERT_SCHEDULE', schedule)
      } else {
        commit('UPSERT_SCHEDULE', { id, enabled })
      }
      dispatch('showNotification', {
        type: 'success',
        message: enabled ? 'Schedule enabled.' : 'Schedule disabled.'
      }, { root: true })
    } catch (error) {
      dispatch('showNotification', {
        type: 'error',
        message: error?.message || 'Couldn’t toggle that schedule.'
      }, { root: true })
      throw error
    } finally {
      commit('SET_RUNTIME_BUSY', { scope: 'schedule', id, action, busy: false })
    }
  },

  // ── Jobs / history ────────────────────────────────────────────────────
  async loadJobs({ commit, state }, filters = {}) {
    const status = filters.status !== undefined ? filters.status : state.jobFilter
    const page = filters.page || state.jobPage
    const pageSize = filters.pageSize || state.jobPageSize
    commit('SET_JOB_FILTER', status)
    commit('SET_JOB_PAGE', page)
    commit('SET_JOB_PAGE_SIZE', pageSize)
    commit('SET_JOBS_LOADING', true)
    commit('SET_JOBS_ERROR', null)
    try {
      const result = await backupApi.listJobs({
        status,
        scheduleId: filters.scheduleId,
        page,
        pageSize
      })
      commit('SET_JOBS', result)
      return result
    } catch (error) {
      if (error instanceof BackupServiceUnavailable) return unavailable(commit, EMPTY_PAGE)
      commit('SET_JOBS_ERROR', error?.message || 'Failed to load history')
      throw error
    } finally {
      commit('SET_JOBS_LOADING', false)
    }
  },

  async downloadJob({ commit, dispatch }, { jobId, suggestedName }) {
    commit('SET_RUNTIME_BUSY', { scope: 'job', id: jobId, action: 'download', busy: true })
    try {
      const filename = await backupApi.downloadJob(jobId, suggestedName)
      dispatch('showNotification', {
        type: 'success',
        message: `Saved ${filename}.`
      }, { root: true })
      return filename
    } catch (error) {
      if (error instanceof BackupAdapterStub) {
        dispatch('showNotification', {
          type: 'info',
          message: `Downloads from ${error.adapter || 'object-storage targets'} are coming soon.`
        }, { root: true })
      } else {
        dispatch('showNotification', {
          type: 'error',
          message: error?.message || 'Couldn’t download that backup.'
        }, { root: true })
      }
      throw error
    } finally {
      commit('SET_RUNTIME_BUSY', { scope: 'job', id: jobId, action: 'download', busy: false })
    }
  },

  // ── Targets ──────────────────────────────────────────────────────────
  async loadTargets({ commit }) {
    commit('SET_TARGETS_LOADING', true)
    commit('SET_TARGETS_ERROR', null)
    try {
      const list = await backupApi.listTargets()
      commit('SET_TARGETS', list)
      return list
    } catch (error) {
      if (error instanceof BackupServiceUnavailable) return unavailable(commit, [])
      commit('SET_TARGETS_ERROR', error?.message || 'Failed to load targets')
      throw error
    } finally {
      commit('SET_TARGETS_LOADING', false)
    }
  },

  async createTarget({ commit, dispatch }, payload) {
    try {
      const target = await backupApi.createTarget(payload)
      commit('UPSERT_TARGET', target)
      dispatch('showNotification', {
        type: 'success',
        message: `Target “${payload.name}” added.`
      }, { root: true })
      return target
    } catch (error) {
      if (error instanceof BackupAdapterStub) {
        dispatch('showNotification', {
          type: 'info',
          message: `${error.adapter || 'This target type'} is coming soon — your config wasn’t saved.`
        }, { root: true })
      } else {
        dispatch('showNotification', {
          type: 'error',
          message: error?.message || 'Couldn’t add that target.'
        }, { root: true })
      }
      throw error
    }
  },

  async updateTarget({ commit, dispatch }, { id, payload }) {
    try {
      const target = await backupApi.updateTarget(id, payload)
      commit('UPSERT_TARGET', target)
      dispatch('showNotification', {
        type: 'success',
        message: 'Target updated.'
      }, { root: true })
      return target
    } catch (error) {
      if (error instanceof BackupAdapterStub) {
        dispatch('showNotification', {
          type: 'info',
          message: `${error.adapter || 'This target type'} is coming soon — your edit wasn’t saved.`
        }, { root: true })
      } else {
        dispatch('showNotification', {
          type: 'error',
          message: error?.message || 'Couldn’t update that target.'
        }, { root: true })
      }
      throw error
    }
  },

  async deleteTarget({ commit, dispatch }, id) {
    commit('SET_RUNTIME_BUSY', { scope: 'target', id, action: 'delete', busy: true })
    try {
      await backupApi.deleteTarget(id)
      commit('REMOVE_TARGET', id)
      dispatch('showNotification', {
        type: 'success',
        message: 'Target deleted.'
      }, { root: true })
    } catch (error) {
      dispatch('showNotification', {
        type: 'error',
        message: error?.message || 'Couldn’t delete that target.'
      }, { root: true })
      throw error
    } finally {
      commit('SET_RUNTIME_BUSY', { scope: 'target', id, action: 'delete', busy: false })
    }
  },

  // ── Restores ─────────────────────────────────────────────────────────
  async loadRestores({ commit }) {
    commit('SET_RESTORES_LOADING', true)
    commit('SET_RESTORES_ERROR', null)
    try {
      const result = await backupApi.listRestores()
      commit('SET_RESTORES', result)
      return result
    } catch (error) {
      if (error instanceof BackupServiceUnavailable) return unavailable(commit, EMPTY_PAGE)
      commit('SET_RESTORES_ERROR', error?.message || 'Failed to load restores')
      throw error
    } finally {
      commit('SET_RESTORES_LOADING', false)
    }
  },

  /**
   * Restore handshake step 1 — fetch the HMAC `confirmation_token`. We DO
   * NOT cache this: the token has a 300s TTL and is bound to the mode +
   * target_app, so each open of the modal asks for a fresh one.
   */
  async fetchRestorePreview(_ctx, jobId) {
    return backupApi.getRestorePreview(jobId)
  },

  /**
   * Restore handshake step 2 — perform the restore.
   * Returns the restore record on success; throws the typed
   * `RestoreConfirmationRejected` if the modal needs to re-prompt.
   */
  async performRestore({ commit, dispatch }, { jobId, payload }) {
    commit('SET_RUNTIME_BUSY', { scope: 'restore', id: jobId, action: 'perform', busy: true })
    try {
      const restore = await backupApi.performRestore(jobId, payload)
      if (restore) commit('UPSERT_RESTORE', restore)
      dispatch('showNotification', {
        type: 'success',
        message: payload.mode === 'in_place'
          ? 'Restore in progress — we’ll email you when it finishes.'
          : 'Restore-to-new-instance queued — track progress in Restores.'
      }, { root: true })
      return restore
    } catch (error) {
      if (error instanceof RestoreConfirmationRejected) {
        // Re-prompt path — leave the toast to the modal, it knows the context.
      } else if (error instanceof BackupAdapterStub) {
        dispatch('showNotification', {
          type: 'info',
          message: `Restores from ${error.adapter || 'this target type'} are coming soon.`
        }, { root: true })
      } else {
        dispatch('showNotification', {
          type: 'error',
          message: error?.message || 'Couldn’t start that restore.'
        }, { root: true })
      }
      throw error
    } finally {
      commit('SET_RUNTIME_BUSY', { scope: 'restore', id: jobId, action: 'perform', busy: false })
    }
  },

  // ── DB-instance discovery (cluster-api → backup-api bridge) ──────────
  async loadDbInstances({ commit }, filters = {}) {
    commit('SET_DB_INSTANCES_LOADING', true)
    commit('SET_DB_INSTANCES_ERROR', null)
    try {
      const list = await backupApi.listDatabaseInstances(filters)
      commit('SET_DB_INSTANCES', list)
      commit('SET_BRIDGE_READY', true)
      return list
    } catch (error) {
      if (error instanceof BackupBridgeNotReady) {
        commit('SET_DB_INSTANCES', [])
        commit('SET_BRIDGE_READY', false)
        // Not a hard error — the wizard will fall back gracefully.
        commit('SET_DB_INSTANCES_ERROR', null)
        return []
      }
      commit('SET_DB_INSTANCES_ERROR', error?.message || 'Failed to load databases')
      commit('SET_BRIDGE_READY', false)
      throw error
    } finally {
      commit('SET_DB_INSTANCES_LOADING', false)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
